import React from 'react';
import { Box, Group, Button, Stack } from '@mantine/core';
import { IJournalResponseInput, QuestionOptions, IConditionalMultiselectOptions } from '../../../../global/entities/Journal/Journal';

interface ConditionalMultiselectQuestionProps {
    questionId: number;
    response: IJournalResponseInput | undefined;
    onResponseChange: (questionId: number, updates: Partial<IJournalResponseInput>) => void;
    options: QuestionOptions;
    currentLang: 'en' | 'fr';
    readOnly?: boolean;
}

export const ConditionalMultiselectQuestion: React.FC<ConditionalMultiselectQuestionProps> = ({
    questionId,
    response,
    onResponseChange,
    options,
    currentLang,
    readOnly = false
}) => {
    const primaryResponse = response?.responseValue;
    const secondaryResponse = response?.subResponseValue;

    const componentOptions = options as IConditionalMultiselectOptions;

    const primaryLabel = currentLang === 'en' ? componentOptions.primaryLabelEn : componentOptions.primaryLabelFr;
    const secondaryLabel = currentLang === 'en' ? componentOptions.secondaryLabelEn : componentOptions.secondaryLabelFr;
    const primaryOptions = currentLang === 'en' ? componentOptions.primaryOptionsEn : componentOptions.primaryOptionsFr;
    const secondaryOptions = currentLang === 'en' ? componentOptions.secondaryOptionsEn : componentOptions.secondaryOptionsFr;

    // Handle primary button click (yes/no)
    const handlePrimaryClick = (value: string) => {
        if (readOnly) return;
        
        if (primaryResponse === value) {
            // Toggle off - reset everything
            onResponseChange(questionId, { 
                responseValue: '',
                subResponseValue: ''
            });
        } else {
            // Toggle on - just set primary value without changing isSkipped
            onResponseChange(questionId, { 
                responseValue: value,
                subResponseValue: '' // Reset sub-response when primary changes
            });
        }
    };

    // Handle secondary option click
    const handleSecondaryClick = (option: string) => {
        if (readOnly) return;
        
        if (secondaryResponse === option) {
            // Toggle off the secondary option
            onResponseChange(questionId, { 
                subResponseValue: '',
            });
        } else {
            // Select the secondary option - question is now complete
            onResponseChange(questionId, { 
                subResponseValue: option,
            });
        }
    };

    return (
        <Stack gap="md">
            {/* Primary yes/no question */}
            <Group>
                <Button
                    variant={primaryResponse === 'yes' ? 'filled' : 'outline'}
                    color={primaryResponse === 'yes' ? 'blue' : 'gray'}
                    onClick={() => handlePrimaryClick('yes')}
                >
                    {primaryLabel}
                </Button>
                <Button
                    variant={primaryResponse === 'no' ? 'filled' : 'outline'}
                    color={primaryResponse === 'no' ? 'blue' : 'gray'}
                    onClick={() => handlePrimaryClick('no')}
                >
                    {secondaryLabel}
                </Button>
            </Group>

            {/* Conditional sub-question for YES */}
            {primaryResponse === 'yes' && (
                <Box mt="md" p="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderRadius: 4 }}>
                    <Group wrap="wrap">
                        {primaryOptions.map((option: any, index: any) => (
                            <Button
                                key={index}
                                variant={secondaryResponse === option ? 'filled' : 'outline'}
                                color={secondaryResponse === option ? 'blue' : 'gray'}
                                onClick={() => handleSecondaryClick(option)}
                                size="sm"
                                mb="xs"
                            >
                                {option}
                            </Button>
                        ))}
                    </Group>
                </Box>
            )}

            {/* Conditional sub-question for NO */}
            {primaryResponse === 'no' && (
                <Box mt="md" p="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderRadius: 4 }}>
                    <Group wrap="wrap">
                        {secondaryOptions.map((option: any, index: any) => (
                            <Button
                                key={index}
                                variant={secondaryResponse === option ? 'filled' : 'outline'}
                                color={secondaryResponse === option ? 'blue' : 'gray'}
                                onClick={() => handleSecondaryClick(option)}
                                size="sm"
                                mb="xs"
                            >
                                {option}
                            </Button>
                        ))}
                    </Group>
                </Box>
            )}
        </Stack>
    );
}; 